import { DollarSign } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { MetricCard } from '../common/MetricCard';
import { EmptyState } from '../common/EmptyState';
import { getCategoryLabel } from '../../constants/expenseCategories';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

interface RevenueTabProps {
  revenue: number;
  expenses: number;
  netProfit: number;
  hstCollected: number;
  hstOnExpenses: number;
  hstOwing: number;
  revenueByMonth: { month: string; revenue: number; expenses: number }[];
  expensesByCategory: { category: string; amount: number }[];
  period: string;
  setPeriod: (p: string) => void;
}

const PERIODS = [
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'YTD' },
];

function fmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

export function RevenueTab({ revenue, expenses, netProfit, hstCollected, hstOnExpenses, hstOwing, revenueByMonth, expensesByCategory, period, setPeriod }: RevenueTabProps) {
  const chartData = revenueByMonth.map(r => ({
    ...r,
    label: fmtMonth(r.month),
  }));

  const catData = expensesByCategory.map(c => ({
    ...c,
    label: getCategoryLabel(c.category),
  }));

  return (
    <div>
      {/* Period selector */}
      <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 6, padding: 2, marginBottom: 20, width: 'fit-content' }}>
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)} style={{
            padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
            background: period === p.value ? palette.g800 : 'transparent',
            color: period === p.value ? palette.white : palette.g500,
          }}>{p.label}</button>
        ))}
      </div>

      {/* Metric cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <MetricCard label="Revenue" value={`$${fmt(revenue)}`} />
        <MetricCard label="Expenses" value={`$${fmt(expenses)}`} />
        <MetricCard
          label="Net Profit"
          value={`$${fmt(netProfit)}`}
          delta={netProfit >= 0 ? 'Positive' : 'Loss'}
          deltaType={netProfit >= 0 ? 'up' : 'down'}
        />
        <MetricCard
          label="HST Owing"
          value={`$${fmt(hstOwing)}`}
          sub={`Collected: $${fmt(hstCollected)} | Credits: $${fmt(hstOnExpenses)}`}
        />
      </div>

      {/* Charts */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Revenue vs Expenses line chart */}
        <div style={{
          flex: 2, background: palette.white, borderRadius: 10,
          border: `1px solid ${palette.g150}`, padding: '20px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginBottom: 16 }}>Revenue vs Expenses</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={palette.g100} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: palette.g500 }} />
                <YAxis tick={{ fontSize: 11, fill: palette.g500 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`$${Number(v ?? 0).toLocaleString()}`, '']} />
                <Line type="monotone" dataKey="revenue" stroke={palette.accent} strokeWidth={2} dot={false} name="Revenue" />
                <Line type="monotone" dataKey="expenses" stroke={palette.red} strokeWidth={2} dot={false} name="Expenses" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<DollarSign size={22} />} title="No revenue data" subtitle="Revenue will appear as invoices are paid." />
          )}
        </div>

        {/* Expense breakdown bar chart */}
        <div style={{
          flex: 1, background: palette.white, borderRadius: 10,
          border: `1px solid ${palette.g150}`, padding: '20px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginBottom: 16 }}>Expense Breakdown</div>
          {catData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={catData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={palette.g100} />
                <XAxis type="number" tick={{ fontSize: 11, fill: palette.g500 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10.5, fill: palette.g600 }} width={100} />
                <Tooltip formatter={(v) => [`$${Number(v ?? 0).toLocaleString()}`, 'Amount']} />
                <Bar dataKey="amount" fill={palette.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<DollarSign size={22} />} title="No expenses" subtitle="Expenses will appear once logged." />
          )}
        </div>
      </div>

      {/* Monthly summary table */}
      <div style={{
        background: palette.white, borderRadius: 10,
        border: `1px solid ${palette.g150}`, overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${palette.g150}`, fontSize: 14, fontWeight: 600, color: palette.g800 }}>
          Monthly Summary
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Month', 'Revenue', 'Expenses', 'Net', 'HST Collected', 'HST on Expenses', 'HST Owing'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', fontSize: 11.5, fontWeight: 600, color: palette.g500,
                  textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: h === 'Month' ? 'left' : 'right',
                  borderBottom: `1px solid ${palette.g150}`,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {revenueByMonth.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 0 }}><EmptyState icon={<DollarSign size={22} />} title="No data yet" subtitle="Monthly summaries will appear as you use CrewBit." /></td></tr>
            ) : revenueByMonth.map(r => {
              const net = r.revenue - r.expenses;
              return (
                <tr key={r.month}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F3F4F6')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g700, borderBottom: `1px solid ${palette.g100}` }}>{fmtMonth(r.month)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g700, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>${fmt(r.revenue)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g700, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>${fmt(r.expenses)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: net >= 0 ? palette.green : palette.red, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>${fmt(net)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g500, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>—</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g500, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>—</td>
                  <td style={{ padding: '10px 14px', fontSize: 13, color: palette.g500, textAlign: 'right', borderBottom: `1px solid ${palette.g100}` }}>—</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
